import React, { useCallback } from 'react';
import ExitIcon from '@moda/icons/exit-16';
import { Button, Clickable, ControlLink, Input, PasswordInput, Stack, Text } from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { useConvertGuest } from '../../hooks/useConvertGuest';
import { usePasswordValidation } from '../../hooks/usePasswordValidation';
import { useUser } from '../../hooks/useUser';
import { ModalOverlay } from '../Modal';
import { hrefFor } from '../../routers';
import { ConvertGuestModalProps } from './ConvertGuestModal';

type RegisterFormValues = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

const VALIDATIONS = {
  firstName: {
    required: {
      message: 'Please enter first name.',
      value: true
    }
  },
  lastName: {
    required: {
      message: 'Please enter last name.',
      value: true
    }
  }
};

export const ConvertGuestLegacyModal: React.FC<ConvertGuestModalProps> = ({
  onAuth,
  onCloseModal,
  show,
  title
}) => {
  const {
    user: { email: userEmail }
  } = useUser();
  const { error: errorAuth, handleConvertGuest } = useConvertGuest();
  const { passwordValidation } = usePasswordValidation();

  const onSubmit = useCallback(
    (values: RegisterFormValues) => {
      handleConvertGuest({
        email: values.email,
        password: values.password,
        first_name: values.firstName,
        last_name: values.lastName
      }).then(onAuth);
    },
    [handleConvertGuest, onAuth]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: { ...VALIDATIONS, password: passwordValidation }
  });

  return (
    <ModalOverlay onClose={onCloseModal || noop} show={show}>
      <div className="ConvertGuestModal">
        <Clickable aria-label="Close" className="ConvertGuestModal__close" onClick={onCloseModal}>
          <ExitIcon />
        </Clickable>
        <form onSubmit={handleSubmit}>
          <Stack space={4}>
            <Text treatment="h4" family="serif">
              {title}
            </Text>
            <Text treatment="h6">
              Create an account to save your shipment and payment preferences, check your order
              status, and more.
            </Text>

            {errorAuth && (
              <div className="ConvertGuestModal__error" role="alert">
                {errorAuth}
              </div>
            )}

            <Input defaultValue={userEmail || ''} disabled name="email" type="email" />

            <Stack direction="horizontal" space={2}>
              <Input
                className="ConvertGuestModal__input"
                error={fieldErrors.firstName?.message || Boolean(errorAuth)}
                id="firstName"
                name="firstName"
                placeholder="First Name"
                type="text"
                maxLength={50}
                focus
              />
              <Input
                className="ConvertGuestModal__input"
                error={fieldErrors.lastName?.message || Boolean(errorAuth)}
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                type="text"
                maxLength={50}
              />
            </Stack>

            <PasswordInput
              error={fieldErrors.password?.message || Boolean(errorAuth)}
              focus
              name="password"
              placeholder="Please enter your password"
            />

            <Button type="submit">Save and create account</Button>

            <Text treatment="body2">
              To opt out, <ControlLink to={hrefFor.UnsubscribePage()}>unsubscribe here</ControlLink>{' '}
              or in our emails.
            </Text>
          </Stack>
        </form>
      </div>
    </ModalOverlay>
  );
};
